import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { predefinedPopularScreens } from './predefinedPopularScreens';

// Better semantic icon imports
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SavingsIcon from '@mui/icons-material/Savings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WorkIcon from '@mui/icons-material/Work';

const MiniPopularScreens = () => {
    const navigate = useNavigate();

    // More specific icon mapping based on actual screen logic
    const getScreenIcon = (screenName, category) => {
        // Specific screen mappings
        const screenIconMap = {
            'Overbought': <TrendingUpIcon fontSize="small" />, // High momentum/price
            'Oversold': <TrendingDownIcon fontSize="small" />, // Low momentum/price
            'Near 200-Day SMA': <TrendingFlatIcon fontSize="small" />, // Near average
            'Large Cap Value': <AccountBalanceIcon fontSize="small" />, // Established value
            'Deep Value': <SavingsIcon fontSize="small" />, // Deep discount
            'Quality Value': <AttachMoneyIcon fontSize="small" />, // Quality + value
            'Momentum': <RocketLaunchIcon fontSize="small" />, // Fast movement
            'Large Cap Leaders': <SpeedIcon fontSize="small" />, // Market leaders
            'Tech Value': <WorkIcon fontSize="small" />, // Tech sector
        };

        if (screenIconMap[screenName]) {
            return screenIconMap[screenName];
        }

        // Category fallbacks
        const categoryIcons = {
            technical: <ShowChartIcon fontSize="small" />,
            value: <AttachMoneyIcon fontSize="small" />,
            growth: <RocketLaunchIcon fontSize="small" />,
            sector: <WorkIcon fontSize="small" />
        };

        return categoryIcons[category] || <ShowChartIcon fontSize="small" />;
    };

    // Handle screen click navigation
    const handleScreenClick = (screen) => {
        const params = new URLSearchParams();

        Object.entries(screen.filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                params.append(key, value.join(','));
            } else {
                params.append(key, value);
            }
        });

        navigate(`/screener?${params.toString()}`);
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                {/* Header */}
                <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: { xs: 1.5, sm: 2 }
                    }}
                >
                    Popular Screens
                </Typography>

                {/* Chips Container */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row', // Always row
                    flexWrap: 'wrap', // Always wrap
                    gap: { xs: 0.75, sm: 1 },
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    {predefinedPopularScreens.map((screen) => (
                        <Chip
                            key={screen.id}
                            clickable
                            icon={getScreenIcon(screen.name, screen.category)}
                            label={screen.name}
                            onClick={() => handleScreenClick(screen)}
                            sx={{
                                borderColor: screen.color,
                                color: screen.color,
                                backgroundColor: 'transparent',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                height: { xs: '26px', sm: '28px' },
                                minWidth: 'auto',
                                maxWidth: { xs: 'calc(50% - 6px)', sm: '150px' },
                                transition: 'all 0.2s ease-in-out',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: screen.color,
                                    color: '#ffffff',
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 8px ${screen.color}20`,
                                    '& .MuiChip-icon': {
                                        color: '#ffffff'
                                    }
                                },
                                '&:active': {
                                    transform: 'translateY(0px)',
                                },
                                '& .MuiChip-icon': {
                                    color: screen.color,
                                    fontSize: { xs: '14px', sm: '16px' },
                                    transition: 'color 0.2s ease-in-out'
                                },
                                '& .MuiChip-label': {
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    px: { xs: 1, sm: 1.25 },
                                    fontWeight: 500
                                }
                            }}
                        />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

export default MiniPopularScreens;